import { Close, ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { type ReactElement, useState } from 'react';

import { AsyncJobStatusContent } from './AsyncJobStatusContent';
import { useAsyncJobStatusContext } from './AsyncJobStatusContext';
import { getAsyncJobStatusPresentation } from './AsyncJobStatusPresentation';

export const AsyncJobStatusWidget = (): ReactElement | null => {
  const theme = useTheme();
  const { job, config, dismiss } = useAsyncJobStatusContext();

  const [isExpanded, setIsExpanded] = useState(true);
  const [isFailureModalOpen, setIsFailureModalOpen] = useState(false);

  if (job === null || config === null) {
    return null;
  }

  const presentation = getAsyncJobStatusPresentation(job, config);
  const canDismiss = !presentation.isInProgress;

  const headerAccentColor = (): string => {
    switch (presentation.tone) {
      case 'info':
        return theme.palette.SlabIndigo[500];
      case 'warning':
        return theme.palette.SlabYellow[600];
      case 'success':
        return theme.palette.SlabGreen[500];
      case 'error':
      default:
        return theme.palette.SlabRed[500];
    }
  };

  const { failureDetails } = config;
  const FailureDetails = failureDetails?.Component;
  const FailurePreview = failureDetails?.PreviewComponent;
  const failurePreview =
    failureDetails !== undefined &&
    FailurePreview !== undefined &&
    presentation.hasFailedItems &&
    isExpanded &&
    !isFailureModalOpen ? (
      <FailurePreview
        jobId={job.id}
        summaryFailedCount={presentation.failedCount}
        isProcessing={presentation.isInProgress}
        actionLabel={failureDetails.actionLabel}
        onViewFailures={(): void => setIsFailureModalOpen(true)}
      />
    ) : null;

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: theme.zIndex.snackbar + 1,
            pointerEvents: 'auto',
          }}
        >
          <Paper
            elevation={8}
            sx={{
              width: 380,
              borderRadius: 2,
              overflow: 'hidden',
              border: `1px solid ${theme.palette.SlabGray.Stroke}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.25,
                backgroundColor: theme.palette.SlabGray[50],
                borderBottom: isExpanded ? `1px solid ${theme.palette.SlabGray.Stroke}` : 'none',
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.SlabGray[100],
                },
              }}
              onClick={(): void => setIsExpanded((previous) => !previous)}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: headerAccentColor(),
                    flexShrink: 0,
                  }}
                />
                <Typography variant='body2' fontWeight={600} noWrap sx={{ flexShrink: 0 }}>
                  {config.title}
                </Typography>
                {!isExpanded && (
                  <Typography variant='body2' color='text.secondary' noWrap sx={{ flexShrink: 0 }}>
                    {presentation.compactLabel}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                {!isExpanded && presentation.isInProgress && (
                  <CircularProgress
                    size={16}
                    thickness={5}
                    sx={{
                      color:
                        presentation.tone === 'warning'
                          ? theme.palette.SlabYellow[600]
                          : theme.palette.SlabIndigo[500],
                      mr: 0.5,
                    }}
                  />
                )}

                {canDismiss && (
                  <IconButton
                    size='small'
                    onClick={(event): void => {
                      event.stopPropagation();
                      dismiss();
                    }}
                    sx={{ color: theme.palette.SlabGray[500], p: 0.25 }}
                  >
                    <Close sx={{ fontSize: 18 }} />
                  </IconButton>
                )}

                <IconButton size='small' sx={{ color: theme.palette.SlabGray[500], p: 0.25 }}>
                  {isExpanded ? (
                    <ExpandLess sx={{ fontSize: 20 }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </Box>
            </Box>

            <Collapse in={isExpanded}>
              <Box sx={{ pt: 2 }}>
                <AsyncJobStatusContent
                  presentation={presentation}
                  failureActionLabel={failureDetails?.actionLabel}
                  onViewFailures={(): void => setIsFailureModalOpen(true)}
                  failurePreview={failurePreview}
                />
              </Box>
            </Collapse>
          </Paper>
        </motion.div>
      </AnimatePresence>

      {FailureDetails !== undefined && (
        <FailureDetails
          isOpen={isFailureModalOpen}
          onClose={(): void => setIsFailureModalOpen(false)}
          jobId={job.id}
          summaryFailedCount={presentation.failedCount}
          isProcessing={presentation.isInProgress}
        />
      )}
    </>
  );
};
